import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FaBoxOpen, FaMoneyBillWave, FaShoppingCart } from 'react-icons/fa';
import api from '../services/api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Line, Doughnut } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const DealerDashboardPage = () => {
  const [myStocks, setMyStocks] = useState([]);
  const [mySales, setMySales] = useState([]);
  const [sellQuantity, setSellQuantity] = useState({});
  const [sellPrices, setSellPrices] = useState({}); // Fiyat state'i
  const [stats, setStats] = useState({
    totalStock: 0,
    dailyRevenue: 0,
    dailySold: 0
  });
  
  // Grafik Verileri
  const [salesTrendData, setSalesTrendData] = useState(null);
  const [productDistData, setProductDistData] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [stocksRes, salesRes] = await Promise.all([
          api.get('/dealers/stock'),
          api.get('/dealers/sales')
        ]);
        setMyStocks(stocksRes.data);
        setMySales(salesRes.data);

        // İstatistikleri Hesapla
        const totalStock = stocksRes.data.reduce((acc, item) => acc + Number(item.stock_quantity), 0);
        
        // Sadece bugünkü satışları filtrele
        const todayStr = new Date().toLocaleDateString('tr-TR');
        const todaySales = salesRes.data.filter(s => new Date(s.sale_date).toLocaleDateString('tr-TR') === todayStr);

        const dailyRevenue = todaySales.reduce((acc, item) => acc + Number(item.total_price), 0);
        const dailySold = todaySales.reduce((acc, item) => acc + Number(item.quantity), 0);

        setStats({ totalStock, dailyRevenue, dailySold });
      } catch (error) {
        console.error(error);
      }
    };
    loadData();
  }, []);

  // Satış verileri değiştiğinde grafikleri güncelle
  useEffect(() => {
    if (!mySales) return;

    // 1. Satış Trendi (Günlük Ciro)
    const salesByIsoDate = {};
    mySales.forEach(sale => {
      const dateObj = new Date(sale.sale_date);
      const isoDate = dateObj.toISOString().split('T')[0]; // YYYY-MM-DD
      salesByIsoDate[isoDate] = (salesByIsoDate[isoDate] || 0) + Number(sale.total_price);
    });

    const sortedDates = Object.keys(salesByIsoDate).sort();
    const trendLabels = sortedDates.map(d => new Date(d).toLocaleDateString('tr-TR'));
    const trendValues = sortedDates.map(d => salesByIsoDate[d]);

    setSalesTrendData({
      labels: trendLabels,
      datasets: [
        {
          label: 'Günlük Ciro (TL)',
          data: trendValues,
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          tension: 0.3
        }
      ]
    });

    // 2. Ürün Dağılımı (Satış Adedi)
    const salesByProduct = {};
    mySales.forEach(sale => {
      salesByProduct[sale.product_name] = (salesByProduct[sale.product_name] || 0) + Number(sale.quantity);
    });

    setProductDistData({
      labels: Object.keys(salesByProduct),
      datasets: [
        {
          label: 'Satış Adedi',
          data: Object.values(salesByProduct),
          backgroundColor: [
            'rgba(255, 99, 132, 0.6)',
            'rgba(54, 162, 235, 0.6)',
            'rgba(255, 206, 86, 0.6)',
            'rgba(75, 192, 192, 0.6)',
            'rgba(153, 102, 255, 0.6)',
            'rgba(255, 159, 64, 0.6)',
          ],
        }
      ]
    });
  }, [mySales]);

  const handleSell = async (stock) => {
    const qty = parseInt(sellQuantity[stock.id] || 0);
    // Fiyatı belirle: Kullanıcı girdiyse onu al, yoksa varsayılan fiyatı kullan
    const price = parseFloat(sellPrices[stock.id] !== undefined ? sellPrices[stock.id] : stock.selling_price);

    if (qty <= 0 || qty > stock.stock_quantity) {
      toast.warning('Geçersiz miktar.');
      return;
    }

    if (isNaN(price) || price < 0) {
      toast.warning('Geçersiz fiyat.');
      return;
    }

    if (!window.confirm(`${stock.product_name} ürününden ${qty} adet, birim fiyatı ${price} TL üzerinden satış yapılacak. Onaylıyor musunuz?`)) return;

    try {
      await api.post('/dealers/sale', {
        product_id: stock.product_id,
        quantity: qty,
        sale_price: price // Dinamik fiyat
      });
      toast.success('Satış başarılı.');
      
      // Verileri yenile
      const [stocksRes, salesRes] = await Promise.all([
        api.get('/dealers/stock'),
        api.get('/dealers/sales')
      ]);
      setMyStocks(stocksRes.data);
      setMySales(salesRes.data);
      setSellQuantity({ ...sellQuantity, [stock.id]: '' });
      
      // İstatistikleri Güncelle
      const totalStock = stocksRes.data.reduce((acc, item) => acc + Number(item.stock_quantity), 0);
      
      const todayStr = new Date().toLocaleDateString('tr-TR');
      const todaySales = salesRes.data.filter(s => new Date(s.sale_date).toLocaleDateString('tr-TR') === todayStr);
      
      const dailyRevenue = todaySales.reduce((acc, item) => acc + Number(item.total_price), 0);
      const dailySold = todaySales.reduce((acc, item) => acc + Number(item.quantity), 0);

      setStats({ totalStock, dailyRevenue, dailySold });

    } catch (error) {
      toast.error(error.response?.data?.message || 'Satış yapılamadı.');
    }
  };

  return (
    <div className="container-fluid mt-4">
      <h2 className="mb-4">Bayi Paneli</h2>

      {/* Özet Kartları */}
      <div className="row mb-4">
        <div className="col-md-4">
          <div className="card text-white bg-primary mb-3 shadow-sm">
            <div className="card-body d-flex align-items-center justify-content-between">
              <div>
                <h6 className="card-title">Mevcut Stok</h6>
                <h3 className="fw-bold">{stats.totalStock}</h3>
              </div>
              <FaBoxOpen size={40} className="opacity-50" />
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card text-white bg-success mb-3 shadow-sm">
            <div className="card-body d-flex align-items-center justify-content-between">
              <div>
                <h6 className="card-title">Günlük Ciro</h6>
                <h3 className="fw-bold">
                  {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(stats.dailyRevenue)}
                </h3>
              </div>
              <FaMoneyBillWave size={40} className="opacity-50" />
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card text-white bg-warning mb-3 shadow-sm">
            <div className="card-body d-flex align-items-center justify-content-between">
              <div>
                <h6 className="card-title">Günlük Satış (Adet)</h6>
                <h3 className="fw-bold">
                  {stats.dailySold}
                </h3>
              </div>
              <FaShoppingCart size={40} className="opacity-50" />
            </div>
          </div>
        </div>
      </div>

      {/* Grafikler */}
      <div className="row mb-4">
        <div className="col-md-8 mb-4 mb-md-0">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-white">
              <h5 className="mb-0">Satış Trendi</h5>
            </div>
            <div className="card-body">
              {salesTrendData && <Line options={{ responsive: true }} data={salesTrendData} />}
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-white">
              <h5 className="mb-0">Ürün Dağılımı (Adet)</h5>
            </div>
            <div className="card-body d-flex justify-content-center align-items-center">
              {productDistData && <Doughnut options={{ responsive: true }} data={productDistData} />}
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        {/* Ürünlerim ve Satış Yap */}
        <div className="col-md-7">
          <div className="card shadow-sm mb-4">
            <div className="card-header bg-white">
              <h5 className="mb-0">Ürünlerim ve Stoklarım</h5>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table align-middle">
                  <thead>
                    <tr>
                      <th>Ürün</th>
                      <th>Stok</th>
                      <th style={{width: '140px'}}>Birim Fiyat</th>
                      <th style={{width: '120px'}}>Satış Adedi</th>
                      <th>İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myStocks.map(stock => (
                      <tr key={stock.id}>
                        <td>{stock.product_name}</td>
                        <td className="fw-bold text-primary">{stock.stock_quantity} {stock.unit}</td>
                        <td>
                          <div className="input-group input-group-sm">
                            <input 
                              type="number" 
                              className="form-control" 
                              min="0"
                              step="0.01"
                              value={sellPrices[stock.id] !== undefined ? sellPrices[stock.id] : stock.selling_price}
                              onChange={(e) => setSellPrices({...sellPrices, [stock.id]: e.target.value})}
                            />
                            <span className="input-group-text">TL</span>
                          </div>
                        </td>
                        <td>
                          <input 
                            type="number" 
                            className="form-control form-control-sm" 
                            min="1" 
                            max={stock.stock_quantity}
                            value={sellQuantity[stock.id] || ''}
                            onChange={(e) => setSellQuantity({...sellQuantity, [stock.id]: e.target.value})}
                          />
                        </td>
                        <td>
                          <button className="btn btn-sm btn-success" onClick={() => handleSell(stock)}>Sat</button>
                        </td>
                      </tr>
                    ))}
                    {myStocks.length === 0 && <tr><td colSpan="5" className="text-center">Tanımlı ürününüz bulunmamaktadır.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Satış Geçmişi */}
        <div className="col-md-5">
          <div className="card shadow-sm">
            <div className="card-header bg-white">
              <h5 className="mb-0">Satış Geçmişim</h5>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table className="table table-sm table-hover mb-0">
                  <thead className="bg-white sticky-top shadow-sm" style={{ zIndex: 1 }}>
                    <tr>
                      <th className="px-3">Tarih</th>
                      <th>Ürün</th>
                      <th>Adet</th>
                      <th className="px-3">Tutar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mySales.map(sale => (
                      <tr key={sale.id}>
                        <td className="px-3 text-muted">{new Date(sale.sale_date).toLocaleDateString('tr-TR')}</td>
                        <td>{sale.product_name}</td>
                        <td>{sale.quantity}</td>
                        <td className="fw-bold">{new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(sale.total_price)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DealerDashboardPage;
